import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtEur, fmtDate, errDetail } from "../lib/api";
import { useApp } from "../lib/store";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { Sparkles, Upload, ClipboardPaste, Loader2, AlertTriangle, AlertCircle, Info,
  CheckCircle2, Trash2, RefreshCw, FileText, Mail, Image as ImageIcon, ChevronDown, ChevronUp } from "lucide-react";

const STATUS_COLORS = {
  New: "bg-gray-100 text-gray-700", Analyzed: "bg-blue-50 text-blue-700",
  Committed: "bg-emerald-50 text-emerald-700", Discarded: "bg-gray-100 text-gray-400",
  Error: "bg-red-50 text-red-700",
};
const KIND_ICON = { email_text: Mail, image: ImageIcon, file: FileText };
const CHECK_ICON = { error: AlertCircle, warning: AlertTriangle, info: Info };
const CHECK_STYLE = {
  error: "bg-red-50 border-red-200 text-red-700",
  warning: "bg-amber-50 border-amber-200 text-amber-800",
  info: "bg-blue-50 border-blue-100 text-blue-700",
};

function ConfidenceBadge({ value }) {
  if (value == null) return null;
  const pct = Math.round(value * 100);
  const cls = pct >= 75 ? "bg-emerald-50 text-emerald-700" : pct >= 50 ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-700";
  return <Badge className={`${cls} border-0 font-mono`} data-testid="oi-confidence">{pct}%</Badge>;
}

function ChecksPanel({ checks, t }) {
  if (!checks?.length) {
    return (
      <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2" data-testid="oi-checks-ok">
        <CheckCircle2 className="h-4 w-4" />{t("oi_no_checks")}
      </div>
    );
  }
  return (
    <div className="space-y-1.5" data-testid="oi-checks">
      {checks.map((c, i) => {
        const Icon = CHECK_ICON[c.level] || Info;
        return (
          <div key={i} className={`flex items-start gap-2 text-xs border rounded-lg px-2.5 py-1.5 ${CHECK_STYLE[c.level]}`} data-testid={`oi-check-${c.code}`}>
            <Icon className="h-3.5 w-3.5 mt-0.5 shrink-0" /><span>{c.message}</span>
          </div>
        );
      })}
    </div>
  );
}

function ReviewDialog({ item, accounts, articles, onClose, onChanged, t, canWrite }) {
  const navigate = useNavigate();
  const [p, setP] = useState(item.proposal || { line_items: [] });
  const [busy, setBusy] = useState("");
  const [showSource, setShowSource] = useState(false);
  const [source, setSource] = useState(null);
  const editable = canWrite && !["Committed", "Discarded"].includes(item.status);

  const setLine = (i, patch) => setP((prev) => ({
    ...prev, line_items: prev.line_items.map((li, j) => (j === i ? { ...li, ...patch } : li)),
  }));

  const save = async () => {
    setBusy("save");
    try {
      const r = await api.put(`/order-inbox/${item.id}`, { proposal: p });
      toast.success(t("updated"));
      onChanged(r.data);
    } catch (e) { toast.error(errDetail(e)); }
    setBusy("");
  };

  const commit = async () => {
    setBusy("commit");
    try {
      await api.put(`/order-inbox/${item.id}`, { proposal: p });
      const r = await api.post(`/order-inbox/${item.id}/commit`);
      toast.success(`${t("oi_committed_toast")} ${r.data.order_number}`);
      onChanged(null);
      navigate(`/orders/${r.data.id}`);
    } catch (e) { toast.error(errDetail(e)); setBusy(""); }
  };

  const discard = async () => {
    setBusy("discard");
    try {
      await api.post(`/order-inbox/${item.id}/discard`, {});
      toast.success(t("oi_discarded_toast"));
      onChanged(null);
    } catch (e) { toast.error(errDetail(e)); setBusy(""); }
  };

  const reanalyze = async () => {
    setBusy("reanalyze");
    try {
      const r = await api.post(`/order-inbox/${item.id}/reanalyze`);
      setP(r.data.proposal || { line_items: [] });
      toast.success(t("oi_reanalyzed"));
      onChanged(r.data, true);
    } catch (e) { toast.error(errDetail(e)); }
    setBusy("");
  };

  const loadSource = async () => {
    if (!showSource && source == null) {
      try { const r = await api.get(`/order-inbox/${item.id}`); setSource(r.data.text || "—"); } catch { setSource("—"); }
    }
    setShowSource((s) => !s);
  };

  const accArticles = articles.filter((a) => !p.account_id || a.account_id === p.account_id);
  const hasError = (item.checks || []).some((c) => c.level === "error" && !(c.code === "no_account" && p.account_id));

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="oi-review-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-[#F5A623]" />{t("oi_review")} — {item.filename}
            <ConfidenceBadge value={item.confidence} />
            <Badge className={`${STATUS_COLORS[item.status]} border-0`}>{t(`oi_status_${item.status.toLowerCase()}`)}</Badge>
          </DialogTitle>
          <DialogDescription>{t("oi_review_desc")}</DialogDescription>
        </DialogHeader>

        {item.status === "Error" ? (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">{item.error || t("oi_error_generic")}</div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="col-span-2">
                <Label className="text-xs">{t("ord_customer")} *</Label>
                <select data-testid="oi-account-select" disabled={!editable}
                  className="w-full h-9 rounded-md border border-gray-200 bg-white px-2 text-sm mt-1"
                  value={p.account_id || ""} onChange={(e) => setP({ ...p, account_id: e.target.value, account_name: (accounts.find((a) => a.id === e.target.value) || {}).name || "" })}>
                  <option value="">–</option>
                  {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
                {item.account_match?.method === "none" && item.account_match.alternatives?.length > 0 && (
                  <p className="text-[11px] text-gray-500 mt-1">{t("oi_suggestions")}: {item.account_match.alternatives.map((a) => a.account_name).join(", ")}</p>
                )}
              </div>
              <div>
                <Label className="text-xs">{t("ord_po")}</Label>
                <Input data-testid="oi-po-input" className="h-9 mt-1" disabled={!editable}
                  value={p.po_number || ""} onChange={(e) => setP({ ...p, po_number: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">{t("ord_requested")}</Label>
                <Input data-testid="oi-requested-input" type="date" className="h-9 mt-1" disabled={!editable}
                  value={p.requested_date || ""} onChange={(e) => setP({ ...p, requested_date: e.target.value })} />
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="px-2 py-2 text-left">#</th>
                    <th className="px-2 py-2 text-left">{t("cond_article")}</th>
                    <th className="px-2 py-2 text-left">{t("description")}</th>
                    <th className="px-2 py-2 text-right">{t("quantity")}</th>
                    <th className="px-2 py-2 text-right">{t("art_price")}</th>
                    <th className="px-2 py-2 text-left">{t("ord_requested")}</th>
                    <th className="px-2 py-2 text-left">{t("oi_suggested_date")}</th>
                  </tr>
                </thead>
                <tbody>
                  {(p.line_items || []).map((li, i) => (
                    <tr key={i} className="border-t border-gray-100" data-testid={`oi-line-${i}`}>
                      <td className="px-2 py-1.5 text-gray-400">{i + 1}</td>
                      <td className="px-2 py-1.5 min-w-[180px]">
                        {editable ? (
                          <select className="w-full h-8 rounded border border-gray-200 bg-white px-1 text-xs" data-testid={`oi-line-${i}-article`}
                            value={li.article_id || ""} onChange={(e) => {
                              const art = accArticles.find((a) => a.id === e.target.value);
                              setLine(i, art ? { article_id: art.id, article_number: art.article_number, matched: true, description: li.description || art.name, winding_direction: art.winding_direction } : { article_id: "", matched: false });
                            }}>
                            <option value="">{`${t("ord_select_article")}`}</option>
                            {accArticles.map((a) => <option key={a.id} value={a.id}>{`${a.article_number} – ${a.name}`}</option>)}
                          </select>
                        ) : <span className="font-mono text-xs">{li.article_number || "–"}</span>}
                        {!li.matched && <span className="text-[10px] text-amber-600 flex items-center gap-1 mt-0.5"><AlertTriangle className="h-3 w-3" />{t("oi_unmatched")}</span>}
                      </td>
                      <td className="px-2 py-1.5 min-w-[160px]">
                        <Input className="h-8 text-xs" disabled={!editable} data-testid={`oi-line-${i}-desc`}
                          value={li.description || ""} onChange={(e) => setLine(i, { description: e.target.value })} />
                      </td>
                      <td className="px-2 py-1.5 w-24">
                        <Input className="h-8 text-xs text-right" type="number" disabled={!editable} data-testid={`oi-line-${i}-qty`}
                          value={li.quantity ?? 0} onChange={(e) => setLine(i, { quantity: parseFloat(e.target.value) || 0 })} />
                      </td>
                      <td className="px-2 py-1.5 w-24">
                        <Input className="h-8 text-xs text-right" type="number" step="0.01" disabled={!editable} data-testid={`oi-line-${i}-price`}
                          value={li.unit_price ?? 0} onChange={(e) => setLine(i, { unit_price: parseFloat(e.target.value) || 0 })} />
                      </td>
                      <td className="px-2 py-1.5 w-32">
                        <Input className="h-8 text-xs" type="date" disabled={!editable} data-testid={`oi-line-${i}-date`}
                          value={li.requested_date || ""} onChange={(e) => setLine(i, { requested_date: e.target.value })} />
                      </td>
                      <td className="px-2 py-1.5 text-xs text-gray-500 font-mono whitespace-nowrap">
                        {li.suggested_date || "–"}{li.production_days ? ` (${li.production_days} AT)` : ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {p.promised_date && (
              <p className="text-xs text-gray-500">{t("oi_promised")}: <span className="font-mono font-semibold text-[#1B2A4A]">{p.promised_date}</span></p>
            )}

            <ChecksPanel checks={item.checks} t={t} />

            <button className="text-xs text-gray-500 hover:text-gray-800 flex items-center gap-1" onClick={loadSource} data-testid="oi-source-toggle">
              {showSource ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}{t("oi_source")}
            </button>
            {showSource && (
              <pre className="text-[11px] bg-gray-50 border border-gray-100 rounded-lg p-3 max-h-48 overflow-auto whitespace-pre-wrap" data-testid="oi-source-text">{source ?? "…"}</pre>
            )}
          </div>
        )}

        {editable && (
          <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-100">
            <Button data-testid="oi-commit-btn" className="bg-[#1B2A4A] hover:bg-[#1B2A4A]/90" disabled={!!busy || !p.account_id}
              onClick={commit}>
              {busy === "commit" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />}
              {t("oi_commit")}
            </Button>
            <Button variant="outline" data-testid="oi-save-btn" disabled={!!busy} onClick={save}>{t("save")}</Button>
            <Button variant="outline" data-testid="oi-reanalyze-btn" disabled={!!busy} onClick={reanalyze}>
              {busy === "reanalyze" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}
              {t("oi_reanalyze")}
            </Button>
            <div className="flex-1" />
            {hasError && <span className="text-[11px] text-red-600">{t("oi_has_errors")}</span>}
            <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" data-testid="oi-discard-btn" disabled={!!busy} onClick={discard}>
              <Trash2 className="h-4 w-4 mr-1.5" />{t("oi_discard")}
            </Button>
          </div>
        )}
        {item.status === "Committed" && item.order_id && (
          <Button variant="outline" className="w-fit" data-testid="oi-open-order-btn" onClick={() => navigate(`/orders/${item.order_id}`)}>
            {t("oi_open_order")} {item.order_number}
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function OrderInbox() {
  const { t, canWrite } = useApp();
  const [data, setData] = useState({ items: [], total: 0 });
  const [stats, setStats] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [articles, setArticles] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [selected, setSelected] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    const params = new URLSearchParams({ page_size: 100 });
    if (statusFilter) params.set("status", statusFilter);
    api.get(`/order-inbox?${params}`).then((r) => setData(r.data)).catch(() => {});
    api.get("/order-inbox/stats").then((r) => setStats(r.data)).catch(() => {});
  }, [statusFilter]);

  useEffect(load, [load]);
  useEffect(() => {
    api.get("/accounts?limit=500").then((r) => setAccounts(r.data.sort((a, b) => a.name.localeCompare(b.name)))).catch(() => {});
    api.get("/articles?page_size=200").then((r) => setArticles(r.data.items || [])).catch(() => {});
  }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setAnalyzing(true);
    try {
      const r = await api.post("/order-inbox/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("oi_analyzed_toast"));
      load();
      setSelected(r.data);
    } catch (err) { toast.error(errDetail(err)); }
    setAnalyzing(false);
    e.target.value = "";
  };

  const paste = async () => {
    if (!pasteText.trim()) return;
    setAnalyzing(true);
    try {
      const r = await api.post("/order-inbox/paste", { text: pasteText });
      toast.success(t("oi_analyzed_toast"));
      setPasteOpen(false);
      setPasteText("");
      load();
      setSelected(r.data);
    } catch (err) { toast.error(errDetail(err)); }
    setAnalyzing(false);
  };

  const by = stats?.by_status || {};

  return (
    <div className="space-y-4" data-testid="order-inbox-page">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-3xl font-bold tracking-tight text-gray-900" style={{ fontFamily: "Work Sans" }}>{t("order_inbox")}</h1>
        <div className="flex-1" />
        {canWrite && (
          <>
            <input type="file" ref={fileRef} className="hidden" accept=".pdf,.eml,.msg,.txt,.docx,.xlsx,.png,.jpg,.jpeg" onChange={upload} />
            <Button variant="outline" data-testid="oi-upload-btn" disabled={analyzing} onClick={() => fileRef.current?.click()}>
              {analyzing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}{t("oi_upload")}
            </Button>
            <Button className="bg-[#1B2A4A] hover:bg-[#1B2A4A]/90" data-testid="oi-paste-btn" disabled={analyzing} onClick={() => setPasteOpen(true)}>
              <ClipboardPaste className="h-4 w-4 mr-1.5" />{t("oi_paste")}
            </Button>
          </>
        )}
      </div>
      <p className="text-sm text-gray-500 max-w-2xl -mt-2">{t("oi_page_desc")}</p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[["Analyzed", "oi_stats_open"], ["Committed", "oi_stats_committed"], ["Discarded", "oi_stats_discarded"], ["Error", "oi_stats_error"]].map(([s, key]) => (
          <button key={s} className={`bg-white border rounded-xl p-4 text-left transition-colors ${statusFilter === s ? "border-[#F5A623]" : "border-gray-200 hover:border-gray-300"}`}
            data-testid={`oi-stat-${s.toLowerCase()}`} onClick={() => setStatusFilter(statusFilter === s ? "" : s)}>
            <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold">{t(key)}</p>
            <p className="text-2xl font-bold font-mono text-[#1B2A4A]">{by[s] || 0}</p>
          </button>
        ))}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {data.items.length === 0 ? (
          <div className="p-12 text-center">
            <div className="h-14 w-14 rounded-2xl bg-[#1B2A4A] flex items-center justify-center mx-auto mb-4">
              <Sparkles className="h-7 w-7 text-[#F5A623]" />
            </div>
            <p className="text-sm text-gray-500 max-w-md mx-auto" data-testid="oi-empty">{t("oi_empty")}</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
              <tr>
                <th className="px-4 py-2.5 text-left">{t("oi_source_col")}</th>
                <th className="px-4 py-2.5 text-left">{t("ord_customer")}</th>
                <th className="px-4 py-2.5 text-left">{t("ord_po")}</th>
                <th className="px-4 py-2.5 text-right">{t("ord_lines")}</th>
                <th className="px-4 py-2.5 text-left">{t("oi_confidence")}</th>
                <th className="px-4 py-2.5 text-left">{t("status")}</th>
                <th className="px-4 py-2.5 text-left">{t("date")}</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((it) => {
                const Icon = KIND_ICON[it.kind] || FileText;
                const warns = (it.checks || []).filter((c) => c.level !== "info").length;
                return (
                  <tr key={it.id} className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer" data-testid={`oi-row-${it.id}`}
                    onClick={() => setSelected(it)}>
                    <td className="px-4 py-2.5"><span className="flex items-center gap-2"><Icon className="h-4 w-4 text-gray-400" /><span className="truncate max-w-[220px]">{it.filename}</span></span></td>
                    <td className="px-4 py-2.5">{it.proposal?.account_name || <span className="text-gray-400">–</span>}</td>
                    <td className="px-4 py-2.5 font-mono text-xs">{it.proposal?.po_number || "–"}</td>
                    <td className="px-4 py-2.5 text-right font-mono">{it.proposal?.line_items?.length || 0}</td>
                    <td className="px-4 py-2.5"><span className="flex items-center gap-1.5"><ConfidenceBadge value={it.confidence} />
                      {warns > 0 && <span className="text-[11px] text-amber-600 flex items-center gap-0.5"><AlertTriangle className="h-3 w-3" />{warns}</span>}</span></td>
                    <td className="px-4 py-2.5">
                      <Badge className={`${STATUS_COLORS[it.status]} border-0`}>{t(`oi_status_${it.status.toLowerCase()}`)}</Badge>
                      {it.order_number && <span className="text-[11px] text-gray-400 ml-1.5 font-mono">{it.order_number}</span>}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-500">{fmtDate(it.created_at)} · {it.created_by}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={pasteOpen} onOpenChange={setPasteOpen}>
        <DialogContent className="max-w-xl" data-testid="oi-paste-dialog">
          <DialogHeader>
            <DialogTitle>{t("oi_paste")}</DialogTitle>
            <DialogDescription>{t("oi_paste_desc")}</DialogDescription>
          </DialogHeader>
          <Textarea data-testid="oi-paste-text" rows={10} value={pasteText} onChange={(e) => setPasteText(e.target.value)}
            placeholder={t("oi_paste_placeholder")} />
          <Button className="bg-[#1B2A4A] hover:bg-[#1B2A4A]/90 w-fit" data-testid="oi-paste-submit" disabled={analyzing || !pasteText.trim()} onClick={paste}>
            {analyzing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />}{t("oi_analyze")}
          </Button>
        </DialogContent>
      </Dialog>

      {selected && (
        <ReviewDialog item={selected} accounts={accounts} articles={articles} t={t} canWrite={canWrite}
          onClose={() => setSelected(null)}
          onChanged={(updated, keepOpen) => { if (updated && keepOpen) setSelected(updated); else if (updated) setSelected(updated); else setSelected(null); load(); }} />
      )}
    </div>
  );
}
